import { useEffect, useRef, useState } from "preact/hooks";
import { useComputed } from "@preact/signals";
import {
  selectedComposerId,
  selectedPieceKey,
  selectedPiece,
  pieceWorkId,
  mentionsReaderOpen,
  mentionsReaderSource,
  mentionCount,
  openMentionsReader,
  closeMentionsReader,
  focusMentionOnMap,
  composers,
} from "../state/store";
import { fetchHistoricalMentions } from "../data/api";
import { EvidenceBlock } from "./EvidenceBlock";
import { formatHistoricalDate } from "../utils/historicalDate";
import { hasMapLocation } from "../utils/geo";
import type { HistoricalMention } from "../types";

/** The affordance that opens the Reader — a slim banner shown above the list whenever the
 *  current search has at least one grounded mention. This is the discoverability layer the
 *  whole feature hangs on: a composer search finds the memoir passages naming that
 *  composer's music, including the ones with no date or place that can never appear on the
 *  map or the timeline. With a PIECE selected the banner is piece-scoped (the probe in
 *  data/queries.ts already counted only that work's mentions): the composer-wide count
 *  under a piece search read as mentions of the piece, which it wasn't. An unresolved
 *  piece has no work-scoped mentions, so the banner simply stays hidden (count is null). */
export function MentionsTrigger() {
  const composerId = selectedComposerId.value;
  const pieceScoped = pieceWorkId(selectedPieceKey.value) != null;
  const count = mentionCount.value;
  const name = useComputed(() => {
    if (pieceWorkId(selectedPieceKey.value) != null) {
      return selectedPiece.value?.label ?? "this piece";
    }
    const id = selectedComposerId.value;
    const c = id == null ? null : composers.value.find((c) => c.id === id);
    return c ? c.name : "this composer";
  });

  if ((composerId == null && !pieceScoped) || count == null || count <= 0) return null;

  return (
    <button class="mentions-trigger" type="button" onClick={openMentionsReader}>
      <svg class="mentions-trigger-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M4 3h11a3 3 0 0 1 3 3v15l-4-2-4 2V6a3 3 0 0 0-1-2.24V21l-4-2V3zm14 0h2v14.76l-2 1V3z"
        />
      </svg>
      {/* "mentions of performances", never "mentions of the composer": these are grounded
          accounts of concerts featuring the composer's music — not a corpus of everything
          ever written about them, which we couldn't possibly claim. */}
      <span>
        Read <strong>{count.toLocaleString()}</strong>{" "}
        {count === 1 ? "mention of a performance" : "mentions of performances"} of{" "}
        {pieceScoped ? <>{name.value}</> : <>{name.value}&rsquo;s music</>}
      </span>
      <span class="mentions-trigger-hint">in memoirs &amp; letters</span>
    </button>
  );
}

/** Honest one-line "when & where" for a mention — the Reader's analogue of a card's date
 *  row. Both halves are optional: an undated, placeless passage says so rather than faking a
 *  day or a dot (the founding rule of the historical layer). */
function whenWhere(m: HistoricalMention): string {
  const when =
    m.date_precision && m.date_earliest
      ? formatHistoricalDate(m.date_edtf || m.date_earliest, m.date_earliest, m.date_precision)
      : "Undated";
  const where = m.city_name || m.place_raw || null;
  return where ? `${when} · ${where}` : `${when} · place unstated`;
}

const EVENT_TYPE_LABEL: Record<string, string> = {
  public_concert: "Public concert",
  private_salon: "Private salon",
  opera: "Opera",
  rehearsal: "Rehearsal",
  church: "Church",
  unknown: "",
};

function MentionCard({ mention }: { mention: HistoricalMention }) {
  const kind = mention.event_type ? EVENT_TYPE_LABEL[mention.event_type] ?? "" : "";
  const works = mention.program.filter((e) => e.work);

  return (
    <article class="mention-card" role="listitem">
      <div class="mention-meta">
        <span class="mention-when">{whenWhere(mention)}</span>
        {kind && <span class="mention-kind">{kind}</span>}
      </div>

      {mention.title && <h4 class="mention-title">{mention.title}</h4>}

      {works.length > 0 && (
        <ul class="mention-program">
          {works.map((e, i) => (
            <li key={i}>
              {/* Colon separator — same convention as <ProgramList>:
                  "Beethoven: Symphony No. 5". */}
              {e.composers.length > 0 && (
                <span class="mention-composer">
                  {e.composers.map((c) => c.name).join(", ")}
                  {": "}
                </span>
              )}
              <span class="mention-work">{e.work}</span>
            </li>
          ))}
        </ul>
      )}

      <EvidenceBlock
        source={mention.source}
        witness={mention.witness}
        quote={mention.quote}
        excerpt={mention.evidence_excerpt}
        locator={mention.locator}
      />

      {mention.concert_id != null && hasMapLocation(mention) && (
        <button
          class="mention-map-link"
          type="button"
          onClick={() => focusMentionOnMap(mention)}
        >
          Show on map ↗
        </button>
      )}
    </article>
  );
}

const PAGE = 50;

/** The Reader drawer: a source-agnostic reading surface for one composer's grounded mentions
 *  across every memoir/letters source. Decoupled from the map and the time slider on purpose —
 *  an undated, placeless passage is a first-class citizen here, which is exactly why it can't
 *  live in the main list. Opened by <MentionsTrigger>. */
export function MentionsReader() {
  const open = mentionsReaderOpen.value;
  const source = mentionsReaderSource.value; // non-null ⇒ SOURCE mode
  const composerId = source ? null : selectedComposerId.value;
  // PIECE mode: a resolved piece filter scopes the Reader to that work's mentions — the
  // same scope the trigger advertised. Source mode ignores it (a whole-memoir read).
  const workId = source ? null : pieceWorkId(selectedPieceKey.value);
  const name = useComputed(() => {
    if (mentionsReaderSource.value) return mentionsReaderSource.value.title;
    if (pieceWorkId(selectedPieceKey.value) != null) {
      return selectedPiece.value?.label ?? "this piece";
    }
    const id = selectedComposerId.value;
    const c = id == null ? null : composers.value.find((c) => c.id === id);
    return c ? c.name : "this composer";
  });
  // The query key all modes share — a source key, a work id, or a composer id.
  const queryKey = source
    ? `src:${source.key}`
    : workId != null
      ? `work:${workId}:comp:${composerId ?? ""}`
      : `comp:${composerId ?? ""}`;

  const [mentions, setMentions] = useState<HistoricalMention[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [undatedOnly, setUndatedOnly] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // (Re)load whenever the drawer opens, the target (composer OR source) changes, or the
  // undated filter flips.
  useEffect(() => {
    if (!open || (composerId == null && workId == null && source == null)) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setMentions([]);
    fetchHistoricalMentions(
      { composer: composerId, work: workId, source: source?.key ?? null, undated: undatedOnly },
      { limit: PAGE, offset: 0, signal: controller.signal },
    )
      .then((page) => {
        setMentions(page.mentions);
        setTotal(page.total);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, queryKey, undatedOnly]);

  // Esc closes; lock the background scroll while the drawer is open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeMentionsReader();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  async function loadMore() {
    if ((composerId == null && workId == null && source == null) || loading || mentions.length >= total) {
      return;
    }
    setLoading(true);
    try {
      const page = await fetchHistoricalMentions(
        { composer: composerId, work: workId, source: source?.key ?? null, undated: undatedOnly },
        { limit: PAGE, offset: mentions.length },
      );
      setMentions((prev) => [...prev, ...page.mentions]);
      setTotal(page.total);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div class="mentions-overlay" onClick={closeMentionsReader}>
      <div
        class="mentions-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={
          source
            ? `Performance mentions from ${name.value}`
            : workId != null
              ? `Mentions of performances of ${name.value}`
              : `Mentions of performances of ${name.value}'s music`
        }
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="mentions-header">
          <div class="mentions-header-text">
            <h2 class="mentions-heading">
              {source
                ? name.value
                : workId != null
                  ? `Performances of ${name.value}`
                  : `Performances of ${name.value}’s music`}
            </h2>
            <p class="mentions-sub">
              {source
                ? "Listed below is every performance this source mentions, quoted verbatim, including undated or placeless passages that don’t appear on the map."
                : workId != null
                  ? "Mentions of concerts & performances featuring this piece, quoted from memoirs & letters — including undated or placeless passages that don’t appear on the map."
                  : "Mentions of concerts & performances featuring this composer’s music, quoted from memoirs & letters — including undated or placeless passages that don’t appear on the map."}
            </p>
          </div>
          <button
            class="mentions-close"
            type="button"
            aria-label="Close"
            onClick={closeMentionsReader}
          >
            &times;
          </button>
        </header>

        <div class="mentions-toolbar">
          <span class="mentions-count">
            {total.toLocaleString()} performance {total === 1 ? "mention" : "mentions"}
          </span>
          <label class="mentions-undated-toggle">
            <input
              type="checkbox"
              checked={undatedOnly}
              onChange={(e) => setUndatedOnly((e.target as HTMLInputElement).checked)}
            />
            Undated / placeless only
          </label>
        </div>

        <div class="mentions-body">
          {error && <div class="mentions-error">Couldn&rsquo;t load mentions. {error}</div>}
          {!error && mentions.length === 0 && !loading && (
            <div class="mentions-empty">No mentions match.</div>
          )}
          <div class="mentions-list" role="list">
            {mentions.map((m) => (
              <MentionCard key={m.id} mention={m} />
            ))}
          </div>
          {mentions.length < total && (
            <button class="mentions-more" type="button" onClick={loadMore} disabled={loading}>
              {loading ? "Loading…" : `Show more (${(total - mentions.length).toLocaleString()} left)`}
            </button>
          )}
          {loading && mentions.length === 0 && <div class="mentions-loading">Loading…</div>}
        </div>
      </div>
    </div>
  );
}
